import { useEffect, useMemo, useState } from 'react';
import { Grid } from 'gridjs-react';
import { h } from 'gridjs';
import 'gridjs/dist/theme/mermaid.css';
import type { OrderFilters, OrderRecord } from '../types';
import { formatCurrency } from '../utils';

interface OrderTableProps {
  data: OrderRecord[];
  isLoading: boolean;
  filters: OrderFilters;
  onFiltersChange: (filters: OrderFilters) => void;
  onView: (record: OrderRecord) => void;
  onEdit: (record: OrderRecord) => void;
  onMarkPaid: (record: OrderRecord) => void;
  onDelete: (record: OrderRecord) => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export function OrderTable({
  data,
  isLoading,
  filters,
  onFiltersChange,
  onView,
  onEdit,
  onMarkPaid,
  onDelete,
  selectedIds,
  onSelectionChange,
}: OrderTableProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const [statusValue, setStatusValue] = useState<OrderFilters['status']>(filters.status ?? 'all');
  const [dateFrom, setDateFrom] = useState(filters.date_from ?? '');
  const [dateTo, setDateTo] = useState(filters.date_to ?? '');

  useEffect(() => {
    setSearchInput(filters.search ?? '');
  }, [filters.search]);

  useEffect(() => {
    setStatusValue(filters.status ?? 'all');
  }, [filters.status]);

  useEffect(() => {
    setDateFrom(filters.date_from ?? '');
  }, [filters.date_from]);

  useEffect(() => {
    setDateTo(filters.date_to ?? '');
  }, [filters.date_to]);

  const recordMap = useMemo(() => new Map(data.map((item) => [item.id, item])), [data]);

  const gridData = useMemo(
    () =>
      data.map((order, index) => ({
        checkbox: order.id,
        stt: index + 1,
        code: order.code,
        customer: order.customer?.name ?? '—',
        total_amount: order.total_amount,
        created_at: order.created_at,
        status: order.status,
        actions: order.id,
      })),
    [data]
  );

  const checkboxHeaderElement = useMemo(() => {
    const allSelected = data.length > 0 && selectedIds.length === data.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < data.length;
    const checkboxId = 'orders-select-all-checkbox';

    setTimeout(() => {
      const checkbox = document.getElementById(checkboxId) as HTMLInputElement | null;
      if (checkbox) checkbox.indeterminate = someSelected;
    }, 0);

    return h('div', { style: { display: 'flex', justifyContent: 'center' } }, [
      h('input', {
        id: checkboxId,
        type: 'checkbox',
        checked: allSelected,
        onChange: (event: Event) => {
          const input = event.target as HTMLInputElement;
          if (input.checked) {
            onSelectionChange(data.map((item) => item.id));
          } else {
            onSelectionChange([]);
          }
        },
      }),
    ]);
  }, [data, selectedIds, onSelectionChange]);

  const formatText = (value: unknown, maxWidth = 200) => {
    const text = typeof value === 'string' ? value : value ? String(value) : '—';
    return h(
      'span',
      {
        className: 'truncate-cell',
        title: text,
        style: { maxWidth: `${maxWidth}px`, display: 'inline-block' },
      },
      text
    );
  };

  const formatCheckbox = (cell: unknown) => {
    const record = recordMap.get(cell as string);
    if (!record) return null;
    return h('div', { style: { display: 'flex', justifyContent: 'center' } }, [
      h('input', {
        type: 'checkbox',
        checked: selectedIds.includes(record.id),
        onChange: (event: Event) => {
          const input = event.target as HTMLInputElement;
          if (input.checked) {
            onSelectionChange([...new Set([...selectedIds, record.id])]);
          } else {
            onSelectionChange(selectedIds.filter((id) => id !== record.id));
          }
        },
      }),
    ]);
  };

  const formatDate = (value: unknown) => {
    if (!value) return formatText('—', 120);
    const date = new Date(String(value));
    return h('span', {}, date.toLocaleString('vi-VN'));
  };

  const formatStatus = (value: unknown) => {
    const status = value as OrderRecord['status'];
    const map: Record<OrderRecord['status'], { label: string; className: string }> = {
      pending: { label: 'Chờ xử lý', className: 'badge bg-warning' },
      processing: { label: 'Đang xử lý', className: 'badge bg-info' },
      completed: { label: 'Hoàn tất', className: 'badge bg-success' },
      cancelled: { label: 'Đã hủy', className: 'badge bg-secondary' },
    };
    const meta = status ? map[status] : map.pending;
    return h(
      'span',
      {
        className: meta.className,
        style: { minWidth: 90, display: 'inline-block', textAlign: 'center' },
      },
      meta.label
    );
  };

  const formatActions = (cell: unknown) => {
    const record = recordMap.get(cell as string);
    if (!record) return null;
    return h('div', { className: 'table-actions' }, [
      h(
        'button',
        {
          className: 'btn btn-sm',
          title: 'Xem chi tiết',
          onClick: () => onView(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h('i', { className: 'ti ti-eye' })
      ),
      h(
        'button',
        {
          className: 'btn btn-sm',
          title: 'Chỉnh sửa',
          onClick: () => onEdit(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h('i', { className: 'ti ti-edit' })
      ),
      h(
        'button',
        {
          className: 'btn btn-sm',
          title: 'Đánh dấu đã thanh toán',
          disabled: record.status === 'completed',
          onClick: () => onMarkPaid(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h('i', { className: 'ti ti-cash' })
      ),
      h(
        'button',
        {
          className: 'btn btn-sm btn-danger',
          title: 'Hủy đơn',
          onClick: () => onDelete(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h('i', { className: 'ti ti-trash' })
      ),
    ]);
  };

  const columns = [
    { id: 'checkbox', name: checkboxHeaderElement, formatter: formatCheckbox, sort: false, width: '60px' },
    { id: 'stt', name: '#', formatter: (value: unknown) => h('span', {}, String(value ?? '')), sort: false, width: '60px' },
    { id: 'code', name: 'Mã đơn', formatter: (value: unknown) => formatText(value, 160), width: '160px' },
    { id: 'customer', name: 'Khách hàng', formatter: (value: unknown) => formatText(value, 220), width: '220px' },
    {
      id: 'total_amount',
      name: 'Tổng tiền',
      formatter: (value: unknown) => formatCurrency(Number(value ?? 0)),
      width: '150px',
    },
    { id: 'created_at', name: 'Ngày tạo', formatter: formatDate, width: '200px' },
    { id: 'status', name: 'Trạng thái', formatter: formatStatus, sort: false, width: '140px' },
    { id: 'actions', name: 'Hành động', formatter: formatActions, sort: false, width: '160px' },
  ];

  const filterFieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 160,
  };

  const filterLabelStyle: React.CSSProperties = {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#94a3b8',
  };

  const roundedControlStyle: React.CSSProperties = {
    borderRadius: '999px',
  };

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="toolbar-actions" style={{ gap: '12px', flexWrap: 'wrap', alignItems: 'center', display: 'flex' }}>
          <label style={filterFieldStyle}>
            <span style={filterLabelStyle}>Trạng thái</span>
            <select
              className="status-filter__select"
              value={statusValue}
              style={roundedControlStyle}
              onChange={(event) => {
                const nextStatus = event.target.value as OrderFilters['status'];
                setStatusValue(nextStatus);
                onFiltersChange({ ...filters, status: nextStatus, search: searchInput, date_from: dateFrom, date_to: dateTo });
              }}
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="pending">Chờ xử lý</option>
              <option value="processing">Đang xử lý</option>
              <option value="completed">Hoàn tất</option>
              <option value="cancelled">Đã hủy</option>
            </select>
          </label>

          <label style={{ ...filterFieldStyle, minWidth: 220 }}>
            <span style={filterLabelStyle}>Tìm kiếm mã đơn / khách hàng</span>
            <input
              type="search"
              className="form-control form-control-sm toolbar-input"
              style={roundedControlStyle}
              placeholder="Nhập mã đơn, tên công ty..."
              value={searchInput}
              onChange={(event) => {
                const value = event.target.value;
                setSearchInput(value);
                onFiltersChange({ ...filters, search: value, status: statusValue, date_from: dateFrom, date_to: dateTo });
              }}
            />
          </label>

          <label style={filterFieldStyle}>
            <span style={filterLabelStyle}>Lọc từ ngày</span>
            <input
              type="date"
              className="form-control form-control-sm"
              style={roundedControlStyle}
              value={dateFrom}
              onChange={(event) => {
                const value = event.target.value;
                setDateFrom(value);
                onFiltersChange({ ...filters, date_from: value, date_to: dateTo, status: statusValue, search: searchInput });
              }}
              aria-label="Từ ngày"
            />
          </label>

          <label style={filterFieldStyle}>
            <span style={filterLabelStyle}>Đến ngày</span>
            <input
              type="date"
              className="form-control form-control-sm"
              style={roundedControlStyle}
              value={dateTo}
              onChange={(event) => {
                const value = event.target.value;
                setDateTo(value);
                onFiltersChange({ ...filters, date_to: value, date_from: dateFrom, status: statusValue, search: searchInput });
              }}
              aria-label="Đến ngày"
            />
          </label>
        </div>
      </div>

      <div className="vuexy-table-wrapper">
        {isLoading ? (
          <p className="text-muted">Đang tải dữ liệu…</p>
        ) : data.length === 0 ? (
          <div className="card card-body text-center text-muted">Chưa có đơn hàng nào.</div>
        ) : (
          <Grid
            data={gridData}
            columns={columns}
            search={false}
            sort
            pagination={{ limit: 10, summary: true }}
            className={{ table: 'vuexy-table' }}
            language={{
              pagination: {
                previous: '‹',
                next: '›',
                showing: 'Hiển thị',
                results: () => 'kết quả',
              },
            }}
          />
        )}
      </div>
    </div>
  );
}


